import { AlertCircle, CheckCircle, ChevronDown, ChevronUp, XCircle } from 'lucide-react';

import { ClientHealthSummary, IncidentStatus, ServiceStatus } from '@/types/service/serviceHealth';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface ClientHealthCardProps {
  client: ClientHealthSummary;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

const ClientHealthCard = ({ client, isExpanded, onToggleExpand }: ClientHealthCardProps) => {
  const getStatusIcon = (status: ServiceStatus) => {
    switch (status) {
      case ServiceStatus.OPERATIONAL:
        return <CheckCircle className='h-5 w-5 text-emerald-600' />;
      case ServiceStatus.DEGRADED:
        return <AlertCircle className='h-5 w-5 text-amber-600' />;
      case ServiceStatus.DOWN:
        return <XCircle className='h-5 w-5 text-red-600' />;
    }
  };

  const getStatusBadge = (status: ServiceStatus) => {
    switch (status) {
      case ServiceStatus.OPERATIONAL:
        return (
          <Badge className='bg-emerald-500 hover:bg-emerald-600 text-white'>Operational</Badge>
        );
      case ServiceStatus.DEGRADED:
        return <Badge className='bg-amber-500 hover:bg-amber-600 text-white'>Degraded</Badge>;
      case ServiceStatus.DOWN:
        return <Badge className='bg-red-500 hover:bg-red-600 text-white'>Down</Badge>;
    }
  };

  // Calculate total ongoing incidents across all environments
  const totalOngoingIncidents = client.environments.reduce((total, env) => {
    return total + env.incidents.filter((inc) => inc.status === IncidentStatus.ONGOING).length;
  }, 0);

  return (
    <Card className='hover:shadow-lg transition-shadow flex flex-col h-full'>
      <CardHeader className='pb-3'>
        <div className='flex items-start justify-between'>
          <div className='flex items-start gap-3'>
            {getStatusIcon(client.overall_status)}
            <div>
              <CardTitle className='text-lg'>{client.client_description}</CardTitle>
              <p className='text-sm text-muted-foreground'>{client.client}</p>
            </div>
          </div>
          <div className='flex flex-col items-end gap-2'>
            {getStatusBadge(client.overall_status)}
            {totalOngoingIncidents > 0 && (
              <Badge variant='destructive' className='bg-orange-500 hover:bg-orange-600'>
                {totalOngoingIncidents} ongoing incident{totalOngoingIncidents > 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className='flex flex-col flex-grow'>
        {/* Environment Summary */}
        <div className='space-y-2 mb-4 flex-grow'>
          {client.environments.map((env) => {
            const envOngoingIncidents = env.incidents.filter(
              (inc) => inc.status === IncidentStatus.ONGOING,
            ).length;
            const worstUptime =
              env.services.length > 0
                ? Math.min(...env.services.map((s) => s.overall_uptime_percent))
                : 100;

            return (
              <div
                key={env.environment}
                className='grid grid-cols-[120px_1fr_70px] gap-2 items-center text-sm border-b border-gray-100 pb-2 last:border-0'
              >
                <span className='font-medium capitalize truncate'>{env.environment}:</span>
                <span className='text-muted-foreground'>
                  {env.services.length} service{env.services.length !== 1 ? 's' : ''}
                </span>
                <div className='flex items-center gap-2 justify-end'>
                  <span
                    className={`font-medium whitespace-nowrap tabular-nums ${
                      worstUptime >= 90
                        ? 'text-emerald-600'
                        : worstUptime > 0
                          ? 'text-amber-600'
                          : 'text-red-600'
                    }`}
                  >
                    {worstUptime.toFixed(1)}%
                  </span>
                  {envOngoingIncidents > 0 && (
                    <Badge variant='outline' className='text-orange-600 border-orange-600 ml-1'>
                      {envOngoingIncidents}
                    </Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Expand/Collapse Button - anchored to bottom */}
        <Button
          variant='outline'
          size='sm'
          onClick={onToggleExpand}
          className='w-full flex items-center justify-center gap-2 mt-auto'
        >
          {isExpanded ? (
            <>
              <ChevronUp className='h-4 w-4' />
              Hide Details
            </>
          ) : (
            <>
              <ChevronDown className='h-4 w-4' />
              View Full Details
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default ClientHealthCard;
